import React, { useEffect, useState } from "react";

/* Light/dark toggle. The source of truth is the data-theme attribute on
   <html>, which public/index.html sets before first paint (stored choice
   first, OS preference otherwise) — reading it here means React state and
   the document can never disagree. Toggling persists the choice, which from
   then on wins over the OS preference. */

function readTheme() {
  if (typeof document === "undefined") return "light";
  return document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";
}

function SunIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function applyTheme(next) {
  document.documentElement.setAttribute("data-theme", next);
  document.documentElement.setAttribute("data-bs-theme", next);
}

function ThemeToggle() {
  const [theme, setTheme] = useState(readTheme);
  const dark = theme === "dark";

  useEffect(() => {
    // Cross-tab sync: a toggle in one tab fires `storage` in every OTHER tab
    // of the same origin, so all of them follow instead of sitting on the old
    // theme until reload.
    const onStorage = (e) => {
      if (e.key !== "theme") return;
      if (e.newValue === "dark" || e.newValue === "light") {
        applyTheme(e.newValue);
        setTheme(e.newValue);
      }
    };

    // Follow a mid-session OS theme change — but only while the visitor has
    // never made an explicit choice. A stored preference always wins, matching
    // what the boot script in index.html does on the next load.
    const mq = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
    const onOsChange = (e) => {
      let stored = null;
      try { stored = localStorage.getItem("theme"); } catch (err) { /* ignore */ }
      if (stored === "dark" || stored === "light") return;
      const next = e.matches ? "dark" : "light";
      applyTheme(next);
      setTheme(next);
    };

    window.addEventListener("storage", onStorage);
    if (mq && mq.addEventListener) mq.addEventListener("change", onOsChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (mq && mq.removeEventListener) mq.removeEventListener("change", onOsChange);
    };
  }, []);

  function toggle() {
    const next = dark ? "light" : "dark";
    applyTheme(next);
    try {
      localStorage.setItem("theme", next);
    } catch (e) {
      /* storage unavailable (private mode) — theme still applies for this page */
    }
    setTheme(next);
  }

  return (
    <button
      type="button"
      className="hd-theme-btn"
      onClick={toggle}
      aria-pressed={dark}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Switch to light theme" : "Switch to dark theme"}
    >
      {dark ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

export default ThemeToggle;
